import { validateId, formatId } from './validation';
import {
  generateRandomString,
  encrypt,
  decrypt,
  generateKey
} from './encryptionUtil';

// Service to handle user data persistence
const TTS_PREFERENCE_KEY = 'tts_preference'; // New key for TTS preference
const VOICE_MODEL_KEY = 'voice_model_preference'; // New key for voice model preference
const CHAT_MODEL_KEY = 'selected_chat_model'; // New key for chat model preference

// Version tracking for storage format
const STORAGE_VERSION = 'v2'; // Updated version to reflect the change in storage format
const STORAGE_VERSION_KEY = 'storage_version';

const SECURE_STORAGE_KEYS = {
  USER_DATA: 'ud',
  REFRESH_TOKEN: 'rt',
  CLIENT_KEY: 'ck', // Now storing directly without obfuscation
  TOKEN_SALT: 'ts'
};

// Add WeakMap for secure in-memory caching
const userDataCache = new WeakMap();
const cacheKey = {}; // Private object as WeakMap key

// Cache invalidation function
const invalidateCache = () => {
  userDataCache.delete(cacheKey);
};

// Add event listener for storage changes
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === SECURE_STORAGE_KEYS.USER_DATA) {
      invalidateCache();
    }
  });
}

/**
 * Check if a salt exists in localStorage
 * @returns {string|null} The existing salt or null if not found
 */
const checkSaltExists = () => {
  try {
    const salt = localStorage.getItem(SECURE_STORAGE_KEYS.TOKEN_SALT);
    if (!salt) {
      return null;
    }
    return salt;
  } catch (error) {
    console.error('Error checking salt existence:', error);
    return null;
  }
};

/**
 * Get or create a salt for token encryption
 * @returns {string} The salt value
 */
const getSalt = () => {
  const existingSalt = checkSaltExists();

  if (!existingSalt) {
    const newSalt = generateRandomString(32);
    localStorage.setItem(SECURE_STORAGE_KEYS.TOKEN_SALT, newSalt);
    return newSalt;
  }

  return existingSalt;
};

// Use a fixed salt for user data to ensure consistency
const USER_DATA_SALT = 'fixed_salt_for_user_data_v1';

/**
 * @typedef {Object} UserData
 * @property {string} id - User's UUID (publicId)
 * @property {string} [legacyId] - Optional legacy numeric ID
 * @property {string} email - User's email address
 * @property {string} firstName - User's first name
 * @property {number} role - User's role level
 * @property {Object} details - Additional user details
 */

export const userStorage = {
  /**
   * Initialize storage with version tracking
   */
  initialize: () => {
    const currentVersion = localStorage.getItem(STORAGE_VERSION_KEY);
    if (currentVersion !== STORAGE_VERSION) {
      localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION);
    }
  },

  /*
  store and retrieve client key and refresh token in secure storage
  */
  setRefreshTokenAndClientKey: async (refreshToken, clientKey) => {
    try {
      // Validate inputs

      // Validate inputs
      if (!refreshToken || !clientKey) {
        console.error('Missing required token data');
        throw new Error('Missing required token data');
      }
      userStorage.initialize();
      localStorage.setItem(SECURE_STORAGE_KEYS.CLIENT_KEY, clientKey);
      const salt = getSalt();
      const cryptoKey = await generateKey(salt, clientKey);
      const encryptedRefreshToken = await encrypt(refreshToken, cryptoKey);
      localStorage.setItem(SECURE_STORAGE_KEYS.REFRESH_TOKEN, encryptedRefreshToken);
      localStorage.setItem('auth_state', 'authenticated');
      window.dispatchEvent(new CustomEvent('auth-state-change', {
        detail: { state: 'authenticated' }
      }));

      return true;
    } catch (error) {
      console.error('Error storing client key and refresh token:', error);
      throw error;
    }
  },

  getRefreshTokenAndClientKey: async () => {
    try {
      // Get the client key directly from localStorage
      const clientKey = localStorage.getItem(SECURE_STORAGE_KEYS.CLIENT_KEY);
      if (!clientKey) {
        return null;
      }

      // Get the encrypted refresh token
      const encryptedRefreshToken = localStorage.getItem(SECURE_STORAGE_KEYS.REFRESH_TOKEN);
      if (!encryptedRefreshToken) {
        return null;
      }

      // Check if salt exists before attempting to use it
      const existingSalt = checkSaltExists();
      if (!existingSalt) {
        // Clean up invalid state by removing tokens that can't be decrypted
        localStorage.removeItem(SECURE_STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(SECURE_STORAGE_KEYS.CLIENT_KEY);
        return null;
      }

      try {
        // Generate the cryptographic key for decryption
        const cryptoKey = await generateKey(existingSalt, clientKey);

        const refreshToken = await decrypt(encryptedRefreshToken, cryptoKey);
        if (!refreshToken) {
          throw new Error('Empty refresh token after decryption');
        }

        return {
          refreshToken,
          clientKey
        };
      } catch (decryptError) {
        console.error('Decryption failed:', decryptError);

        // If decryption fails, clear tokens and return null
        localStorage.removeItem(SECURE_STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(SECURE_STORAGE_KEYS.CLIENT_KEY);
        localStorage.removeItem(SECURE_STORAGE_KEYS.TOKEN_SALT);

        return null;
      }
    } catch (error) {
      console.error('Error retrieving client key and refresh token:', error);
      return null;
    }
  },

  /**
   * Get stored user data
   * @returns {UserData|null} The user data or null if not found
   * User data example:
   * 
   * {
    "id": "4f75af78-9a24-488e-9906-3622278c934d",
    "internalId": 3,
    "email": "glen@fleurlamont.com",
    "verified": true,
    "role": 40,
    "lastName": "Birkbeck",
    "firstName": "Glen",
    "preferredCurrency": "GBP",
    "preferredLanguage": "en-GB",
    "status": 0,
    "subscriptionType": 20,
    "subscriptionStart": "2025-12-11T01:28:23.737Z",
    "subscriptionEnd": "2025-12-11T01:28:23.737Z",
    "score": 0,
    "lastLogin": "2025-12-11T11:10:20.763Z",
    "levelName": "",
    "credits": 1000,
    "createdAt": "2025-12-11T01:28:23.737Z",
    "updatedAt": "2025-12-11T11:10:20.764Z",
    "isSystemsAdmin": true,
    "isAdmin": true,
    "userRoleRealmArray": [
        {
            "id": 1,
            "name": "default",
            "parentId": 0,
            "description": "Default realm",
            "jsonData": "\"{}\"",
            "inviteCode": "",
            "isActive": true,
            "createdAt": "2025-12-11T01:28:23.444Z",
            "updatedAt": "2025-12-11T01:28:23.444Z"
        }
    ]
}
   */
  getUserData: async () => {
    try {
      // Check cache first
      const cachedData = userDataCache.get(cacheKey);
      if (cachedData) {
        //console.log('userStorage.getUserData - cached data: ', cachedData);
        return cachedData;
      }

      // Get the client key directly from localStorage
      const clientKey = localStorage.getItem(SECURE_STORAGE_KEYS.CLIENT_KEY);
      if (!clientKey) {
        return null;
      }

      const savedUserData = localStorage.getItem(SECURE_STORAGE_KEYS.USER_DATA);
      if (!savedUserData) {
        return null;
      }

      try {
        // Generate the cryptographic key for decryption using the fixed salt
        const cryptoKey = await generateKey(USER_DATA_SALT, clientKey);

        // Decrypt the user data
        const decryptedData = await decrypt(savedUserData, cryptoKey);

        if (!decryptedData) {
          return null;
        }

        // Parse the decrypted JSON
        const userData = JSON.parse(decryptedData);

        // Handle legacy data migration
        if (userData.userId && !userData.id) {
          // Store legacy ID and update to new format
          userData.legacyId = userData.userId;
          userData.id = userData.publicId || userData.userId.toString();
          delete userData.userId;
          // Use skipEventDispatch=true to prevent infinite loop
          await userStorage.setUserData(userData, true);
        }

        // Cache the decrypted data
        userDataCache.set(cacheKey, userData);
        //console.log('userStorage: getUserData: userData:', decryptedData, userData);
        return userData;
      } catch (decryptError) {
        console.error('Error decrypting user data:', decryptError);
        return null;
      }
    } catch (error) {
      console.error('Error retrieving user data:', error);
      return null;
    }
  },

  /**
   * Store user data
   * @param {UserData} userData - The user data to store
   * @param {boolean} [skipEventDispatch=false] - Whether to skip dispatching the user-data-updated event
   */
  setUserData: async (userData, skipEventDispatch = false) => {
    // Invalidate cache when setting new data
    invalidateCache();

    if (!userData) {
      localStorage.removeItem(SECURE_STORAGE_KEYS.USER_DATA);
      return;
    }

    try {
      // Get the client key directly from localStorage
      const clientKey = localStorage.getItem(SECURE_STORAGE_KEYS.CLIENT_KEY);
      if (!clientKey) {
        console.error('No client key available for encrypting user data');
        return;
      }

      // Validate UUID if present
      if (userData.id) {
        validateId(userData.id, {
          allowLegacy: false,
          paramName: 'User ID'
        });
      }

      // Ensure legacy data is properly formatted
      if (userData.legacyId) {
        validateId(userData.legacyId, {
          allowLegacy: true,
          paramName: 'Legacy ID'
        });
        userData.legacyId = formatId(userData.legacyId);
      }

      // Generate a cryptographic key for encryption using the fixed salt
      const cryptoKey = await generateKey(USER_DATA_SALT, clientKey);

      // Encrypt the user data
      //console.log('userStorage.setUserData - encrypting user data: ', userData);
      const encryptedUserData = await encrypt(JSON.stringify(userData), cryptoKey);

      // Store the encrypted data
      localStorage.setItem(SECURE_STORAGE_KEYS.USER_DATA, encryptedUserData);

      // Dispatch event to notify components that user data has been updated
      // Only if skipEventDispatch is false
      if (!skipEventDispatch) {
        window.dispatchEvent(new CustomEvent('user-data-updated', {
          detail: { userData }
        }));
      }
    } catch (error) {
      console.error('Error storing user data:', error);
    }
  },

  /**
   * Clear user data
   */
  clearUserData: (clearAuthData = false) => {
    if (!clearAuthData) {
      localStorage.removeItem(SECURE_STORAGE_KEYS.USER_DATA);
    } else {
      // Clear all secure storage keys
      for (const key in SECURE_STORAGE_KEYS) {
        localStorage.removeItem(SECURE_STORAGE_KEYS[key]);
      }
      localStorage.setItem('auth_state', 'unauthenticated');
    }
    // Invalidate cache when clearing data
    invalidateCache();
    // Dispatch event to notify components that user data has been cleared
    window.dispatchEvent(new CustomEvent('user-data-updated', {
      detail: { userData: null }
    }));

  },

  /**
   * Set a session-only client key for "login as user" (impersonation).
   * The impersonation API does not return a refresh token or client key; user data
   * still needs a client key for encrypt/decrypt. This stores a locally generated
   * key so setUserData/getUserData work for the duration of the session.
   * Logout (clearUserData(true)) will remove it.
   */
  setSessionClientKeyForLoginAsUser: () => {
    const sessionKey = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `login-as-${Date.now()}-${generateRandomString(12)}`;
    localStorage.setItem(SECURE_STORAGE_KEYS.CLIENT_KEY, sessionKey);
  },

  /**
   * Get specific user field
   * @param {string} fieldName - Name of the field to retrieve
   * @returns {*} The field value or null if not found
   */
  getUserField: async (fieldName) => {
    const userData = await userStorage.getUserData();
    //console.log('userStorage.getUserField', fieldName, userData[fieldName])
    return userData ? userData[fieldName] : null;
  },

  /**
   * Get user ID (UUID)
   * @returns {string|null} The user's UUID or null if not found
   */
  getUserId: async () => {
    const userData = await userStorage.getUserData();
    return userData ? userData.id : null;
  },

  /**
   * Get legacy user ID if available
   * @returns {string|null} The legacy ID or null if not found
   */
  getLegacyId: async () => {
    const userData = await userStorage.getUserData();
    return userData ? userData.legacyId : null;
  },

  /**
   * Get user email (specifically needed for token refresh)
   * @returns {string|null} The user's email or null if not found
   */
  getUserEmail: async () => {
    return await userStorage.getUserField('email');
  },

  /**
   * Set email verified status
   */
  setEmailVerified: async () => {
    const userData = await userStorage.getUserData();
    if (userData) {
      userData.verified = true;
      // Use skipEventDispatch=true to prevent infinite loop from setUserData's own event
      await userStorage.setUserData(userData, true);
      // Dispatch user-data-updated event to trigger AuthContext state update
      window.dispatchEvent(new CustomEvent('user-data-updated', {
        detail: { userData }
      }));
    }
  },

  /**
   * Get text-to-speech preference
   * @returns {boolean|null} The TTS preference or null if not set
   */
  getTtsPreference: () => {
    try {
      const preference = localStorage.getItem(TTS_PREFERENCE_KEY);
      if (preference === null) return null;
      return preference === 'true';
    } catch (error) {
      console.error('Error getting TTS preference:', error);
      return null;
    }
  },

  /**
   * Set text-to-speech preference
   * @param {boolean} enabled - Whether TTS should be enabled
   */
  setTtsPreference: (enabled) => {
    try {
      localStorage.setItem(TTS_PREFERENCE_KEY, enabled.toString());
      // Dispatch custom event to trigger re-render in components
      window.dispatchEvent(new CustomEvent('tts-preference-changed', { detail: { enabled } }));
    } catch (error) {
      console.error('Error setting TTS preference:', error);
    }
  },

  /**
   * Get voice model preference
   * @returns {string} The voice model preference or empty string if not set
   */
  getVoiceModel: () => {
    try {
      const model = localStorage.getItem(VOICE_MODEL_KEY);
      return model || ''; // Return empty string if not set (let server choose default)
    } catch (error) {
      console.error('Error getting voice model preference:', error);
      return '';
    }
  },

  /**
   * Set voice model preference
   * @param {string} model - The voice model ID in format 'provider:name'
   */
  setVoiceModel: (model) => {
    try {
      localStorage.setItem(VOICE_MODEL_KEY, model);
      // Dispatch custom event to trigger re-render in components
      window.dispatchEvent(new CustomEvent('voice-model-changed', { detail: { model } }));
    } catch (error) {
      console.error('Error setting voice model preference:', error);
    }
  },

  /**
   * Get chat model preference
   * @returns {Object} The chat model preference with provider and model name, or null if not set
   */
  getChatModel: () => {
    try {
      const modelString = localStorage.getItem(CHAT_MODEL_KEY);
      if (!modelString) {
        return null; // Return null if not set (let server choose default)
      }

      const parts = modelString.split(':');
      if (parts.length === 2) {
        return { provider: parts[0], model: parts[1] };
      }

      // If format is invalid, return null
      return null;
    } catch (error) {
      console.error('Error getting chat model preference:', error);
      return null;
    }
  },

  /**
   * Set chat model preference
   * @param {string} modelString - The model string in format 'provider:model'
   */
  setChatModel: (modelString) => {
    try {
      localStorage.setItem(CHAT_MODEL_KEY, modelString);

      // Parse the model string to get provider and model
      const parts = modelString.split(':');
      if (parts.length === 2) {
        const provider = parts[0];
        const model = parts[1];

        // Dispatch custom event to trigger re-render in components
        window.dispatchEvent(new CustomEvent('chat-model-changed', {
          detail: { provider, model }
        }));
      }
    } catch (error) {
      console.error('Error setting chat model preference:', error);
    }
  },

  deleteRefreshTokenAndKey: () => {
    userStorage.clearUserData();
    // Explicitly set auth state to unauthenticated in localStorage
    localStorage.setItem('auth_state', 'unauthenticated');
  }
};

// Add a test function to verify encryption/decryption
export const testEncryptionDecryption = async () => {
  try {
    console.log('=== TESTING ENCRYPTION/DECRYPTION ===');

    // Generate a test client key
    const testClientKey = generateRandomString(48);
    console.log('Generated test client key:', testClientKey.substring(0, 5) + '...');

    // Generate a test refresh token
    const testRefreshToken = generateRandomString(64);
    console.log('Generated test refresh token:', testRefreshToken.substring(0, 5) + '...');

    // Test obfuscation/deobfuscation
    const keyPrefix = generateRandomString(8);
    const keySuffix = generateRandomString(8);
    const obsClientKey = obfuscateKey(keyPrefix, testClientKey, keySuffix);
    console.log('Obfuscated client key:', obsClientKey.substring(0, 10) + '...');

    const deobsClientKey = deobfuscateKey(obsClientKey);
    console.log('Deobfuscated client key:', deobsClientKey.substring(0, 5) + '...');
    console.log('Obfuscation test passed:', deobsClientKey === testClientKey);

    // Test encryption/decryption with a fixed salt
    const testSalt = generateRandomString(32);
    console.log('Test salt:', testSalt.substring(0, 5) + '...');

    // Generate a key for encryption
    const cryptoKey = await generateKey(testSalt, testClientKey);
    console.log('Generated crypto key for test');

    // Encrypt the test refresh token
    const encryptedToken = await encrypt(testRefreshToken, cryptoKey);
    console.log('Encrypted token:', encryptedToken.substring(0, 10) + '...');

    // Decrypt the token with the same key
    const decryptedToken = await decrypt(encryptedToken, cryptoKey);
    console.log('Decrypted token:', decryptedToken.substring(0, 5) + '...');
    console.log('Encryption test passed:', decryptedToken === testRefreshToken);

    return {
      success: decryptedToken === testRefreshToken,
      obfuscationSuccess: deobsClientKey === testClientKey
    };
  } catch (error) {
    console.error('Encryption test failed:', error);
    return { success: false, error: error.message };
  }
};

// Add a function to clear all tokens and force a re-login
export const clearAllTokensAndForceRelogin = () => {
  console.log('Clearing all tokens and forcing re-login');

  // Clear all secure storage keys
  for (const key in SECURE_STORAGE_KEYS) {
    localStorage.removeItem(SECURE_STORAGE_KEYS[key]);
  }

  // Set auth state to unauthenticated
  localStorage.setItem('auth_state', 'unauthenticated');

  // Dispatch events to notify components
  window.dispatchEvent(new CustomEvent('auth-state-change', {
    detail: { state: 'unauthenticated' }
  }));

  window.dispatchEvent(new CustomEvent('user-data-updated', {
    detail: { userData: null }
  }));

  console.log('All tokens cleared, user will need to re-login');
};

// Expose the test function to the window object for easy testing from the browser console
if (typeof window !== 'undefined') {
  window.testEncryption = testEncryptionDecryption;
  window.clearTokens = () => {
    localStorage.removeItem(SECURE_STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(SECURE_STORAGE_KEYS.CLIENT_KEY);
    localStorage.removeItem(SECURE_STORAGE_KEYS.TOKEN_SALT);
    localStorage.removeItem(SECURE_STORAGE_KEYS.USER_DATA);
    console.log('All tokens and user data cleared from localStorage');
  };

  // Add the new function to the window object
  window.forceRelogin = clearAllTokensAndForceRelogin;
}

export default userStorage; 